import React, { useState, useEffect, memo, useRef } from 'react';
import { logout, getFeaturedGames, getSoccerGames, getNBAGames, getNFLGames, getMLBGames, getNHLGames, getUserBalance, placeBets } from '../services/api';
import browserGuardService from '../services/browserGuard';
import GameDetail from './GameDetail';
import DateNavigator from './DateNavigator';
import DepositModal from './DepositModal';
import WithdrawModal from './WithdrawModal';
import Casino from './Casino';
import DebugPanel from './DebugPanel';
import GeoErrorModal from './GeoErrorModal';
import OnboardingModal from './OnboardingModal';
import { gcExplorerUrl, operatorMeta } from '../config';
import './Dashboard.css';

// Memoized Score component to prevent flickering
const LiveScore = memo(({ score }) => {
  return <span className="team-score">{score}</span>;
}, (prevProps, nextProps) => {
  // Only re-render if score actually changed
  return prevProps.score === nextProps.score;
});

function Dashboard({ user, geoData, onLogout, setGeoData, initialMarket }) {
  console.log('Dashboard render');
  const [balance, setBalance] = useState(user?.balance || 0);
  const [activeTab, setActiveTab] = useState(
    initialMarket === 'casino' ? 'casino' :
    'featured'
  );
  const [selectedSport, setSelectedSport] = useState('all');
  const [betSlip, setBetSlip] = useState([]);
  const [showPromo, setShowPromo] = useState(false);
  const [featuredGames, setFeaturedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [liveScores, setLiveScores] = useState({});
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [lastVerified, setLastVerified] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusColor, setStatusColor] = useState('#4ade80'); // Green by default
  const [debugOpen, setDebugOpen] = useState(false);
  const [geoCountdown, setGeoCountdown] = useState(null);
  const [betConfirmation, setBetConfirmation] = useState(null); // { results: [...] }
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  // Compliance gate: betting/casino are only allowed while the most recent
  // GeoComply check PASSED. geoChecked = at least one check has completed.
  const [geoVerified, setGeoVerified] = useState(false);
  const [geoChecked, setGeoChecked] = useState(false);

  // Load initial balance
  useEffect(() => {
    refreshBalance();

    let unsubscribe = () => {};
    let unsubscribeDebug = () => {};
    let cancelled = false; // guard against state updates after unmount

    // Drive the compliance gate + header status off every check result
    // (login, interval, manual, financial, IP-change). Successes verify; denials
    // and SDK errors un-verify so wagering is blocked until a fresh pass.
    unsubscribeDebug = browserGuardService.subscribeDebug((e) => {
      if (cancelled) return;
      if (e.type === 'success') { setGeoVerified(true); setGeoChecked(true); }
      else if (e.type === 'denied' || e.type === 'sdk_error') { setGeoVerified(false); setGeoChecked(true); }
    });

    // Initialize BrowserGuard when user is available
    if (user?.username) {

        // Subscribe to background updates
        unsubscribe = browserGuardService.subscribe((newLocation, triggerType) => {
            if (cancelled) return;
            console.log('Received background location update:', newLocation, triggerType);
            setGeoData(newLocation);
            setGeoError(null);

            if (triggerType === 'interval' || triggerType === 'ip_change') {
                setStatusMessage("Re-verification Successful");
                setStatusColor('#4ade80');
                const tid = setTimeout(() => setStatusMessage(null), 3000);
                // store so we can clear on unmount
                statusTimerRef.current = tid;
            }
        });

        const alreadySeen = localStorage.getItem(`gc_onboarding_seen_${user.username}`) === 'true';

        const runGeoFlow = async () => {
            await browserGuardService.initialize(user.username, user.operator);
            if (cancelled) return;
            const remaining = browserGuardService.getStoredRemainingMs();
            if (remaining > 0) {
                console.log(`[BrowserGuard] Stored session found (${Math.round(remaining / 1000)}s remaining). Validating...`);
                const location = await browserGuardService.resumeFromStoredState();
                if (location !== null) {
                    if (!cancelled) setGeoData(location);
                    return;
                }
                console.log('[BrowserGuard] Validation failed. Performing fresh check.');
            }
            const result = await browserGuardService.requestGeolocation('login');
            if (cancelled || !result || result.superseded) return;
            if (result.success) {
                setGeoData(result.location);
            } else {
                setGeoError(buildGeoError(result));
            }
        };

        if (!alreadySeen) {
            setShowOnboarding(true);
            setOnboardingStep(1);
        }

        runGeoFlow().catch(err => {
            if (cancelled) return;
            const errorDetails = (err && typeof err === 'object') ? (err.message || JSON.stringify(err)) : err;
            console.error('BrowserGuard init/check failed:', errorDetails);
            setGeoError({ message: errorDetails || 'Verification Failed', canRetry: true });
        });
    }

    return () => {
        cancelled = true;
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        unsubscribe();
        unsubscribeDebug();
        browserGuardService.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Debug loading state changes
  useEffect(() => {
    console.log('Loading state changed to:', loading);
  }, [loading]);
  
  const selectedSportRef = useRef(selectedSport);
  const selectedDateRef = useRef(selectedDate);
  const statusTimerRef = useRef(null);

  // Keep refs in sync
  useEffect(() => {
    selectedSportRef.current = selectedSport;
    selectedDateRef.current = selectedDate;
  }, [selectedSport, selectedDate]);

  useEffect(() => {
    if (geoData) {
      setLastVerified(new Date().toLocaleTimeString());
    }
  }, [geoData]);

  // Drive the geo countdown in the header
  useEffect(() => {
    const tick = () => {
      const svc = browserGuardService;
      if (svc.timerId && svc.timerStartTime && svc.timerInterval) {
        const left = Math.max(0, svc.timerInterval - (Date.now() - svc.timerStartTime));
        setGeoCountdown(Math.ceil(left / 1000));
      } else {
        setGeoCountdown(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSport, selectedDate]);

  // Auto-refresh live games every 5 seconds - silently update scores
  useEffect(() => {
    console.log('Setting up auto-refresh interval');
    // Start the interval immediately
    const interval = setInterval(() => {
      console.log("Interval tick - calling refreshLiveScores");
      refreshLiveScores();
    }, 5000);
    return () => {
      console.log("Clearing interval");
      clearInterval(interval);
    };
  }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps


  // Silent refresh for live scores only
  const refreshLiveScores = async () => {
    console.log('refreshLiveScores() - silent update');
    try {
      let response;
      // Use ref to get current selected sport since this runs in interval/closure
      const sport = selectedSportRef.current;
      
      switch (sport) {
        case 'soccer': response = await getSoccerGames(); break;
        case 'basketball': response = await getNBAGames(); break;
        case 'football': response = await getNFLGames(); break;
        case 'baseball': response = await getMLBGames(); break;
        case 'hockey': response = await getNHLGames(); break;
        case 'all': default: response = await getFeaturedGames();
      }

      if (response.success && response.games) {
        const newScores = {};
        response.games.forEach(game => {
            if (game.isLive || game.isFinal) {
                newScores[game.id] = {
                    awayScore: game.awayScore,
                };
            }
        });
        setLiveScores(prev => ({...prev, ...newScores}));
      }
    } catch (error) {
      console.error('Error refreshing scores:', error);
    }
  };

  const refreshBalance = async () => {
    try {
      const response = await getUserBalance();
      if (response.success) {
        setBalance(response.balance);
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };










  const handleDepositSuccess = async () => {
    // Wait a brief moment to ensure the backend has processed the transaction
    await new Promise(resolve => setTimeout(resolve, 500));
    refreshBalance();
  };

  const handleWithdrawSuccess = async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    refreshBalance();
  };


  const buildGeoError = (result) => {
    let msg = result.error_message || 'Verification Failed';
    let allowRetry = true;
    if (result.troubleshooter && Array.isArray(result.troubleshooter) && result.troubleshooter.length > 0) {
      const messages = result.troubleshooter.filter(m => m.type === 'message' || !m.type);
      if (messages.length > 0) {
        msg = messages.map(m => m.content).join(' ');
        if (messages.some(m => m.retry === false)) allowRetry = false;
      } else msg = result.troubleshooter[0].content;
    }
    if (!msg && result.error_code === 1) msg = 'Restricted Jurisdiction';
    return { message: msg || 'Verification Failed', canRetry: allowRetry };
  };

  const handleGeoRetry = async () => {
    setGeoError(null);
    try {
      // Full reset ensures stale SDK event listeners, stuck promises, and
      // lifecycle flags are all cleared before issuing a new request.
      await browserGuardService.resetForRetry();
      const result = await browserGuardService.requestGeolocation('retry');
      if (!result || result.superseded) return;
      if (result.success) {
        setGeoData(result.location);
      } else {
        setGeoError(buildGeoError(result));
      }
    } catch (error) {
      setGeoError({ message: error.message || 'Verification Failed', canRetry: true });
    }
  };

  const handleOnboardingNext = () => {
    setOnboardingStep(prev => prev + 1);
  };

  const handleOnboardingPrev = () => {
    setOnboardingStep(prev => Math.max(1, prev - 1));
  };

  const handleOnboardingClose = () => {
    localStorage.setItem(`gc_onboarding_seen_${user.username}`, 'true');
    setShowOnboarding(false);
  };

  const loadGames = async () => {
    console.log('loadGames() called - this should NOT happen during score refresh!');
    setLoading(true);
    try {
      let response;

      // Load games based on selected sport filter
      switch (selectedSport) {
        case 'soccer':
          response = await getSoccerGames();
          break;
        case 'basketball':
          response = await getNBAGames();
          break;
        case 'football':
          response = await getNFLGames();
          break;
        case 'baseball':
          response = await getMLBGames();
          break;
        case 'hockey':
          response = await getNHLGames();
          break;
        case 'all':
        default:
          response = await getFeaturedGames();

    }

      if (response.success && response.games) {
        // Filter games by selected date — but if no games fall on the selected
        // day, show everything returned so the grid is never empty for a demo.
        const filteredGames = filterGamesByDate(response.games, selectedDate);
        const gamesToShow = filteredGames.length ? filteredGames : response.games;
        console.log(`Loaded ${gamesToShow.length} games for sport: ${selectedSport}, date: ${selectedDate.toDateString()}`);
        setFeaturedGames(gamesToShow);

    }


    } catch (error) {
      console.error("Error loading games:", error);




    } finally {
      setLoading(false);

    }
  };

  // Filter games by selected date
  const filterGamesByDate = (games, date) => {
    if (!games || games.length === 0) return games;

    const selectedDateStr = date.toDateString();

    return games.filter(game => {
      if (!game.date) {
        // If no date info, show on today
        return selectedDateStr === new Date().toDateString();

    }

      // Parse game date
      const gameDate = new Date(game.date);
      return gameDate.toDateString() === selectedDateStr;

    });
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleLogout = async () => {
    browserGuardService.clearCheckState();
    try {
      await logout();
      onLogout();
    } catch (error) {
      console.error("Logout error:", error);
      onLogout();



    }
  };

  const myBets = [
    { id: 1, game: 'Lakers vs Celtics', pick: 'Lakers -3.5', stake: 50, toWin: 95.45, status: 'pending' },
    { id: 2, game: 'Chiefs ML', pick: 'Chiefs', stake: 100, toWin: 190.91, status: 'pending' },
  ];

  const handleOddsClick = (game, team, odds) => {
    const betId = `${game.id}-${team}`;
    const existingBet = betSlip.find(bet => bet.id === betId);

    if (existingBet) {
      setBetSlip(betSlip.filter(bet => bet.id !== betId));

    } else {

      setBetSlip([...betSlip, {
        id: betId,
        game: `${game.away} @ ${game.home}`,
        team: team,
        odds: odds,
        league: game.league,
        stake: 0 // Initialize stake

    }]);
      alert('Bet added to slip!');

    }
  };

  const removeBet = (betId) => {
    setBetSlip(betSlip.filter(bet => bet.id !== betId));
  };

  const clearBetSlip = () => {
    setBetSlip([]);
  };

  const handleGameClick = (game) => {
    setSelectedGame(game);
  };

  const handleAddToBetSlipFromDetail = (bet) => {
    const existingBet = betSlip.find(b => b.id === bet.id);
    if (!existingBet) {
      setBetSlip([...betSlip, bet]);
      // Show notification
      alert('Bet added to slip!');

    }
  };

  const handlePlaceBet = async () => {
    // Compliance gate: no wagering without a current passing GeoComply check.
    if (!geoVerified) {
        alert("Location not verified. A passing GeoComply check is required before you can place a bet.");
        return;
    }

    const totalStake = betSlip.reduce((sum, bet) => sum + (parseFloat(bet.stake) || 0), 0);

    if (totalStake <= 0) {
        alert("Please enter a stake for your bets.");
        return;
    }

    if (totalStake > balance) {
        alert("Insufficient balance.");
        return;
    }
    // 3. Place Bet (Real API)
    try {
        setLoading(true);
        const response = await placeBets(betSlip);
        if (response.success) {
            browserGuardService.logFinancialTransaction('Bet Placed');
            setBetSlip([]);
            refreshBalance();
            setBetConfirmation({
              results: response.results,
              gcTransactionId: browserGuardService.lastGcTransactionId,
            });
        } else {
            alert(`Failed to place bets: ${response.message}`);
        }
    } catch (error) {
        console.error("Error placing bets:", error);
        alert(error.response?.data?.message || "Error placing bets. Please try again.");
    } finally {
        setLoading(false);
    }
  };

  const operator = operatorMeta(user?.operator);

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <img src="/geocomply-logo.jpg" alt="GeoComply" className="logo-shield" />
            <div className="logo-text-group">
              <span className="logo-brand">Latency Test Demo</span>
              <span className="logo-sub">Sportsbook &amp; Casino · Powered by GeoComply</span>
            </div>
            <span className="operator-badge" title={`GeoComply operator: ${operator.label} (${operator.region})`}>
              <span className="operator-badge-flag">{operator.flag}</span>
              <span className="operator-badge-text">
                <span className="operator-badge-label">{operator.label}</span>
                <span className="operator-badge-region">{operator.region}</span>
              </span>
            </span>
          </div>

          <nav className="main-nav">
            {(initialMarket === 'sportsbook' || initialMarket === 'featured' || !initialMarket) && <>
              <button className={activeTab === 'featured' ? 'active' : ''} onClick={() => setActiveTab('featured')}>
                Sportsbook
              </button>
              <button className={activeTab === 'mybets' ? 'active' : ''} onClick={() => setActiveTab('mybets')}>
                My Bets
              </button>
            </>}
            {initialMarket === 'casino' && (
              <button className={activeTab === 'casino' ? 'active' : ''} onClick={() => setActiveTab('casino')}>
                Casino
              </button>
            )}
          </nav>

          <div className="header-actions">
            <div className="balance-display">
              <span className="balance-label">Balance:</span>
              <span className="balance-amount">${balance.toFixed(2)}</span>
              <div className="balance-actions">
                <button
                  className="deposit-btn"
                  onClick={() => setShowDepositModal(true)}
                  title="Deposit funds"
                >
                  Deposit
                </button>
                <button
                  className="withdraw-btn"
                  onClick={() => setShowWithdrawModal(true)}
                  title="Withdraw funds"
                >
                  Withdraw
                </button>
              </div>
            </div>
            <div className="user-menu">
              <span className="username">{user?.username || 'User'}</span>
              <button className="logout-btn" onClick={handleLogout}>Logout</button>
            </div>

            {/* Geo status widget — far right, next to logout */}
            <div className={`geo-status-widget${geoChecked && !geoVerified ? ' geo-status-widget--failed' : ''}`}>
              <span className="geo-status-icon">{geoChecked && !geoVerified ? '🚫' : '📍'}</span>
              <div className="geo-status-details">
                {geoVerified && geoData?.city ? (
                  <>
                    <span className="geo-status-location">{geoData.city}, {geoData.state}</span>
                    <span className="geo-status-verified">Eligible jurisdiction confirmed</span>
                    <span className="geo-status-verified geo-status-verified--ok">VERIFIED</span>
                  </>
                ) : geoChecked && !geoVerified ? (
                  <>
                    <span className="geo-status-location geo-status-location--failed">Location check failed</span>
                    <span className="geo-status-verified geo-status-verified--fail">NOT VERIFIED — betting blocked</span>
                  </>
                ) : (
                  <span className="geo-status-location">Verifying location…</span>
                )}
                {statusMessage && (
                  <span style={{fontSize: '9px', color: statusColor, fontWeight: 600}}>{statusMessage}</span>
                )}
              </div>
              <div className="geo-status-countdown">
                <span className="geo-status-countdown-label">{geoCountdown != null ? 'Next check in' : ''}</span>
                <span className="geo-status-countdown-val">{geoCountdown != null ? `${geoCountdown}s` : ''}</span>
              </div>
              <button
                className={`geo-monitor-btn${debugOpen ? ' geo-monitor-btn--active' : ''}`}
                onClick={() => setDebugOpen(prev => !prev)}
                title="Show Behind the Scenes"
              >
                <div className="geo-monitor-btn-bars">
                  <span /><span /><span />
                </div>
                <span className="geo-monitor-btn-label">Behind the Scenes</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <GeoErrorModal error={geoError} onRetry={handleGeoRetry} onClose={() => setGeoError(null)} />

      <OnboardingModal
        visible={showOnboarding}
        step={onboardingStep}
        onNext={handleOnboardingNext}
        onPrev={handleOnboardingPrev}
        onSkip={handleOnboardingClose}
      />

      {/* Page body: content + debug panel side by side */}
      <div className="page-body">
      <main className="dashboard-main">
        <div className={`content-wrapper${activeTab === 'casino' ? ' content-wrapper--casino' : ''}`}>
          {/* Sidebar — hidden on casino */}
          <aside className={`sidebar${activeTab === 'casino' ? ' sidebar--hidden' : ''}`}>
            <div className="sidebar-section">
              <h3>Sports</h3>
              <ul className="sports-list">
                <li className={selectedSport === 'all' ? 'active' : ''} onClick={() => setSelectedSport('all')}>
                  <span className="sport-icon">🏆</span>
                  All Sports
                  <span className="count">{featuredGames.length}</span>
                </li>
                <li className={selectedSport === 'soccer' ? 'active' : ''} onClick={() => setSelectedSport('soccer')}>
                  <span className="sport-icon">⚽</span>
                  Soccer
                  <span className="count">World Cup</span>
                </li>
                <li className={selectedSport === 'basketball' ? 'active' : ''} onClick={() => setSelectedSport('basketball')}>
                  <span className="sport-icon">🏀</span>
                  Basketball
                  <span className="count">NBA</span>
                </li>
                <li className={selectedSport === 'football' ? 'active' : ''} onClick={() => setSelectedSport('football')}>
                  <span className="sport-icon">🏈</span>
                  Football
                  <span className="count">NFL</span>
                </li>
                <li className={selectedSport === 'baseball' ? 'active' : ''} onClick={() => setSelectedSport('baseball')}>
                  <span className="sport-icon">⚾</span>
                  Baseball
                  <span className="count">MLB</span>
                </li>
                <li className={selectedSport === 'hockey' ? 'active' : ''} onClick={() => setSelectedSport('hockey')}>
                  <span className="sport-icon">🏒</span>
                  Hockey
                  <span className="count">NHL</span>
                </li>
              </ul>
            </div>
          </aside>

          {/* Main Games Area */}
          <section className="games-section">
            {activeTab === 'featured' && (
              <>
                <div className="section-header">
                  <h2>{selectedSport === 'all' ? 'Featured Games' :
                       selectedSport === 'soccer' ? 'World Cup' :
                       selectedSport === 'basketball' ? 'NBA Games' :
                       selectedSport === 'football' ? 'NFL Games' :
                       selectedSport === 'baseball' ? 'MLB Games' :
                       selectedSport === 'hockey' ? 'NHL Games' : 'Featured Games'}</h2>
                  <div className="view-options">
                    <button className="active">All</button>
                    <button>Spreads</button>
                    <button>Totals</button>
                    <button>Moneyline</button>
                  </div>
                </div>

                {/* Date Navigator - ESPN Style */}
                <DateNavigator
                  selectedDate={selectedDate}
                  onDateChange={handleDateChange}
                />

                {loading ? (
                  <div className="loading-games">
                    <div className="spinner"></div>
                    <p>Loading games...</p>
                  </div>
                ) : featuredGames.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-icon">📅</span>
                    <h3>No Games Scheduled</h3>
                    <p>No games found for {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                    <button className="secondary-btn" onClick={() => setSelectedDate(new Date())}>View Today's Games</button>
                  </div>
                ) : (
                  <div className="games-grid">
                    {featuredGames.map((game) => {
                      const gameKey = `${game.id}-${game.home}-${game.away}`; // Stable key
                      return (
                      <div key={gameKey} className={`game-card ${game.isLive ? 'live-game' : ''}`} onClick={() => handleGameClick(game)} style={{cursor: 'pointer'}}>
                      <div className="game-header">
                        <span className="league-badge">{game.league}</span>
                        {game.isLive ? (
                          <span className="live-indicator">
                            <span className="live-dot"></span>
                            LIVE
                          </span>
                        ) : game.isFinal ? (
                          <span className="final-badge">FINAL</span>
                        ) : (
                          <span className="game-time">{game.time}</span>
                        )}
                      </div>

                      <div className="game-matchup">
                        <div className="team-row">
                          <div className="team-info">
                            {game.awayLogo && <img src={game.awayLogo} alt={game.away} className="team-logo" />}
                            <span className="team-name">{game.away}</span>
                          </div>
                          {(game.isLive || game.isFinal) && (
                            <LiveScore score={liveScores[game.id]?.awayScore || game.awayScore} />
                          )}
                          {!game.isLive && !game.isFinal && (
                            <button
                              className={`odds-btn ${game.awayOdds > 0 ? 'underdog' : 'favorite'} ${betSlip.find(bet => bet.id === `${game.id}-${game.away}`) ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOddsClick(game, game.away, game.awayOdds);
                              }}
                            >
                              {game.awayOdds > 0 ? '+' : ''}{game.awayOdds}
                            </button>
                          )}
                        </div>
                        <div className="team-row">
                          <div className="team-info">
                            {game.homeLogo && <img src={game.homeLogo} alt={game.home} className="team-logo" />}
                            <span className="team-name">{game.home}</span>
                          </div>
                          {(game.isLive || game.isFinal) && (
                            <LiveScore score={liveScores[game.id]?.homeScore || game.homeScore} />
                          )}
                          {!game.isLive && !game.isFinal && (
                            <button
                              className={`odds-btn ${game.homeOdds > 0 ? 'underdog' : 'favorite'} ${betSlip.find(bet => bet.id === `${game.id}-${game.home}`) ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOddsClick(game, game.home, game.homeOdds);
                              }}
                            >
                              {game.homeOdds > 0 ? '+' : ''}{game.homeOdds}
                            </button>
                          )}
                        </div>
                      </div>

                      {game.isLive && (liveScores[game.id]?.clock || game.clock) && (
                        <div className="game-status">
                          {liveScores[game.id]?.clock || game.clock}
                        </div>
                      )}

                      <div className="game-actions">
                        <button className="more-bets-btn" onClick={(e) => {
                          e.stopPropagation();
                          handleGameClick(game);
                        }}>
                          +42 More Bets
                        </button>
                      </div>
                    </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {activeTab === 'mybets' && (
              <>
                <div className="section-header">
                  <h2>My Bets</h2>
                  <div className="bet-filters">
                    <button className="active">Pending</button>
                    <button>Settled</button>
                  </div>
                </div>

                {myBets.length > 0 ? (
                  <div className="bets-list">
                    {myBets.map(bet => (
                      <div key={bet.id} className="bet-card">
                        <div className="bet-header">
                          <span className="bet-status pending">Pending</span>
                          <span className="bet-id">#{bet.id}</span>
                        </div>
                        <div className="bet-details">
                          <h4>{bet.game}</h4>
                          <p className="bet-pick">{bet.pick}</p>
                        </div>
                        <div className="bet-amounts">
                          <div className="amount-item">
                            <span className="label">Stake</span>
                            <span className="value">${bet.stake.toFixed(2)}</span>
                          </div>
                          <div className="amount-item">
                            <span className="label">To Win</span>
                            <span className="value win">${bet.toWin.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <span className="empty-icon">🎫</span>
                    <h3>No Active Bets</h3>
                    <p>Place your first bet to get started</p>
                  </div>
                )}
              </>
            )}
            {activeTab === 'casino' && (
              <Casino
                balance={balance}
                onBalanceChange={setBalance}
                setGeoError={setGeoError}
                buildGeoError={buildGeoError}
                geoVerified={geoVerified}
              />
            )}
          </section>

          {/* Right Column — only on sportsbook and mybets */}
          {(activeTab === 'featured' || activeTab === 'mybets') && <aside className="right-col">
          {/* Betslip */}
          <div className="betslip">
            <div className="betslip-header">
              <h3>Bet Slip</h3>
              {betSlip.length > 0 && (
                <button className="clear-betslip" onClick={clearBetSlip}>Clear All</button>
              )}
            </div>
            <div className="betslip-content">
              {betSlip.length === 0 ? (
                <div className="empty-betslip">
                  <span className="betslip-icon">🎟️</span>
                  <p>Select odds to add to bet slip</p>
                </div>
              ) : (
                <>
                  <div className="betslip-items">
                    {betSlip.map(bet => (
                      <div key={bet.id} className="betslip-item">
                        <div className="betslip-item-header">
                          <span className="betslip-league">{bet.league}</span>
                          <button className="betslip-remove" onClick={() => removeBet(bet.id)}>×</button>
                        </div>
                        <div className="betslip-game">{bet.game}</div>
                        <div className="betslip-pick">
                          <span>{bet.team}</span>
                          <span className="betslip-odds">{bet.odds > 0 ? '+' : ''}{bet.odds}</span>
                        </div>
                        <input
                            type="number"
                            className="betslip-stake"
                            placeholder="Stake amount"
                            value={bet.stake || ''}
                            onChange={(e) => {
                                const newStake = parseFloat(e.target.value) || 0;
                                const updatedSlip = betSlip.map(b => 
                                    b.id === bet.id ? { ...b, stake: newStake } : b
                                );
                                setBetSlip(updatedSlip);
                            }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="betslip-summary">
                    <div className="summary-row">
                      <span>Total Bets:</span>
                      <span>{betSlip.length}</span>
                    </div>
                    {!geoVerified && (
                      <div className="betslip-geo-lock">
                        🔒 Location not verified — a passing GeoComply check is required to place bets.
                      </div>
                    )}
                    <button
                      className="place-bet-btn"
                      onClick={handlePlaceBet}
                      disabled={!geoVerified}
                      title={geoVerified ? 'Place bet' : 'Location must be verified first'}
                    >
                      {geoVerified ? 'Place Bet' : 'Location Not Verified'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          </aside>}
        </div>
      </main>

      {/* Debug panel — pushes content left when open */}
      <DebugPanel isOpen={debugOpen} onClose={() => setDebugOpen(false)} geoData={geoData} />
      </div>{/* end page-body */}

      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-legal">
            <p>Latency Test Demo: Sportsbook &amp; Casino — Powered by GeoComply</p>
          </div>
        </div>
      </footer>

      {/* Game Detail Modal */}
      {selectedGame && (
        <GameDetail
          game={selectedGame}
          onClose={() => setSelectedGame(null)}
          onAddToBetSlip={handleAddToBetSlipFromDetail}
        />
      )}

      {/* Promo Modal */}
      {showPromo && (
        <div className="modal-overlay" onClick={() => setShowPromo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPromo(false)}>×</button>
            <h2>🎁 Risk-Free Bet Up to $1,000</h2>
            <p style={{marginBottom: '16px'}}>New users get their first bet refunded if it loses!</p>
            <div style={{background: 'rgba(13, 110, 253, 0.1)', padding: '20px', borderRadius: '12px', marginBottom: '16px'}}>
              <h3 style={{color: '#58a6ff', marginBottom: '12px'}}>How It Works:</h3>
              <ul style={{color: '#9ca3af', lineHeight: '1.8', paddingLeft: '20px'}}>
                <li>Place your first bet up to $1,000</li>
                <li>If it wins, you keep the winnings!</li>
                <li>If it loses, we'll refund your stake as a free bet</li>
                <li>Use your free bet on any sport</li>
              </ul>
            </div>
            <p style={{fontSize: '12px', color: '#6b7280', marginBottom: '16px'}}>
              Location verification required. Powered by GeoComply BrowserGuard.
            </p>
            <button className="modal-button" onClick={() => setShowPromo(false)}>Claim Offer</button>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal
          onClose={() => setShowDepositModal(false)}
          onSuccess={handleDepositSuccess}
        />
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <WithdrawModal
          onClose={() => setShowWithdrawModal(false)}
          onSuccess={handleWithdrawSuccess}
          currentBalance={balance}
        />
      )}

      {/* Bet Confirmation Modal */}
      {betConfirmation && (
        <div className="modal-overlay" onClick={() => setBetConfirmation(null)}>
          <div className="modal-content bet-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setBetConfirmation(null)}>×</button>
            <div className="bet-confirm-icon">✓</div>
            <h2 className="bet-confirm-title">Bet Placed Successfully</h2>
            <p className="bet-confirm-sub">Your transaction has been recorded and verified.</p>
            <div className="bet-confirm-txns">
              {betConfirmation.results.filter(r => r.status === 'placed').map((r, i) => (
                <div key={i} className="bet-confirm-txn-row">
                  <span className="bet-confirm-txn-label">Bet #{r.betId ?? i + 1}</span>
                  <span className="bet-confirm-txn-label" style={{color: 'rgba(255,255,255,0.25)'}}>tx {r.transactionId}</span>
                </div>
              ))}
              {betConfirmation.gcTransactionId && (
                <div className="bet-confirm-txn-row bet-confirm-txn-row--gc">
                  <span className="bet-confirm-txn-label">GeoComply Transaction</span>
                  <a
                    className="bet-confirm-txn-link"
                    href={gcExplorerUrl(betConfirmation.gcTransactionId, user?.operator)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {betConfirmation.gcTransactionId}
                  </a>
                </div>
              )}
            </div>
            <button className="modal-button" onClick={() => setBetConfirmation(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
